import styles from "./Spinner.module.css";

/**
 * Spinner — indeterminate loading indicator.
 *
 * CSS-only rotating arc (no SVG). `size` hardcodes a pixel dimension
 * (xs=14, sm=16, md=20, lg=24) to match Icon and typical inline spinner usage.
 * `tone="brand"` uses --brand; `tone="inherit"` uses currentColor so the
 * spinner picks up whatever color the parent text has — useful for
 * `<Button loading>` where the spinner should match the button label.
 *
 * By default the spinner is a live region (`role="status"` +
 * `aria-label`, default "Loading"). Pass `decorative` when the loading
 * state is already announced by an ancestor (e.g. a `<Button loading>`
 * with `aria-busy` + a visible label) — the spinner then renders purely
 * visually (`aria-hidden`, no role/label) to avoid a redundant
 * announcement.
 */

type Size = "xs" | "sm" | "md" | "lg";
type Tone = "brand" | "inherit";

export type SpinnerProps = {
  size?: Size;
  tone?: Tone;
  /** Accessible label. Defaults to "Loading". Ignored when `decorative`. */
  label?: string;
  /**
   * When true, render the spinner as purely visual: `aria-hidden`, no
   * `role="status"`, no `aria-label`. Use when the loading state is
   * already conveyed elsewhere (an ancestor's `aria-busy` + visible text).
   */
  decorative?: boolean;
  className?: string;
};

export function Spinner({
  size = "md",
  tone = "brand",
  label = "Loading",
  decorative = false,
  className,
}: SpinnerProps) {
  const classes = [styles.spinner, styles[`size-${size}`], styles[`tone-${tone}`], className]
    .filter(Boolean)
    .join(" ");
  if (decorative) {
    return <span className={classes} aria-hidden="true" />;
  }
  return <span className={classes} role="status" aria-label={label} />;
}
